import { Box, Icon, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { FinanceValueObjectsAccountTypeEnum } from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

export interface LedgerAccountFormValues {
  number: string
  name: string
  isDisabled: false
  type: FinanceValueObjectsAccountTypeEnum
  externalId?: string
  legalEntityId?: number
}

export const formValidation = (values: LedgerAccountFormValues) => {
  const errors: Record<string, string> = {}
  if (!values.number) {
    errors.number = i18n.t('finance.account_ledger.new_modal.validation_mandatory')
  }
  if (!values.name) {
    errors.name = i18n.t('finance.account_ledger.new_modal.validation_mandatory')
  }
  if (!values.legalEntityId) {
    errors.legalEntityId = i18n.t('finance.account_ledger.new_modal.validation_mandatory')
  }
  if (isNaN(Number(values.number))) {
    errors.number = i18n.t('finance.account_ledger.new_modal.validation_digits')
  }
  return errors
}

const typeList: FinanceValueObjectsAccountTypeEnum[] = [
  'bank',
  'current_asset',
  'current_liability',
  'equity',
  'expense',
  'income',
  'non_current_asset',
  'non_current_liability',
]

const getLabel = (type: FinanceValueObjectsAccountTypeEnum) => {
  switch (type) {
    case 'bank':
      return i18n.t('finance.account_ledger.new_modal.types.bank')
    case 'current_asset':
      return i18n.t('finance.account_ledger.new_modal.types.current_asset')
    case 'current_liability':
      return i18n.t('finance.account_ledger.new_modal.types.current_liability')
    case 'equity':
      return i18n.t('finance.account_ledger.new_modal.types.equity')
    case 'expense':
      return i18n.t('finance.account_ledger.new_modal.types.expense')
    case 'income':
      return i18n.t('finance.account_ledger.new_modal.types.income')
    case 'non_current_asset':
      return i18n.t('finance.account_ledger.new_modal.types.non_current_asset')
    case 'non_current_liability':
      return i18n.t('finance.account_ledger.new_modal.types.non_current_liability')
  }
}

export const LedgerAccountCreationForm = ({
  form,
  legalEntities,
}: {
  form: ReturnType<typeof useForm<LedgerAccountFormValues>>
  legalEntities: { id: number; currency: string; legalName: string; main: boolean }[]
}) => {
  const typeOptions = useUnsafeMemo(() => {
    return typeList.map((type) => ({
      value: type,
      label: getLabel(type),
    }))
  }, [])

  const [open, onToggle] = useToggle(false)

  const leganEntityOptions = useUnsafeMemo(() => {
    return legalEntities.map((le) => ({
      value: le.id,
      label: le.legalName ?? '',
    }))
  }, [legalEntities])

  return (
    <Box gap={'s16'}>
      <SelectField
        label={i18n.t('finance.account_ledger.new_modal.legal_entity')}
        value={form.values.legalEntityId + ''}
        onChange={(value) => form.setFieldValue('legalEntityId', Number(value))}
        options={leganEntityOptions}
        mandatory
      />
      <TextField
        label={i18n.t('finance.account_ledger.new_modal.account_number')}
        value={form.values.number}
        onChange={(value) => form.setFieldValue('number', value)}
        error={form.errors.number}
        mandatory
      />
      <TextField
        label={i18n.t('finance.account_ledger.new_modal.account_name')}
        value={form.values.name}
        onChange={(value) => form.setFieldValue('name', value)}
        error={form.errors.name}
        mandatory
      />
      <SelectField
        label={i18n.t('finance.account_ledger.new_modal.type')}
        value={form.values.type}
        onChange={(value) => form.setFieldValue('type', value)}
        options={typeOptions}
      />
      <TextField
        secondaryLabel={
          <Tooltip.Alt
            open={open}
            label={i18n.t('finance.account_ledger.new_modal.erp_id_long_description_v2')}
            attachment="bottom center"
            toggle={onToggle}
          >
            <Icon.Medium icon="info-circle-line" color="grey700" />
          </Tooltip.Alt>
        }
        label={i18n.t('finance.account_ledger.new_modal.erp_id')}
        value={form.values.externalId}
        onChange={(value) => form.setFieldValue('externalId', value)}
        description={i18n.t('finance.account_ledger.new_modal.erp_id_description')}
      />
    </Box>
  )
}
